// src/pages/DashboardPage.tsx
import React from 'react';
import { Card, Row, Col, Statistic, Typography } from 'antd';
import { TeamOutlined, FileTextOutlined, AuditOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/useAuthStore';
import { mockClients, mockDocuments, mockAudits } from '../data/mockData';
import AuditsPerMonthChart from '../components/charts/AuditsPerMonthChart';
import AuditsByClientChart from '../components/charts/AuditsByClientChart';
import AuditStatusChart from '../components/charts/AuditStatusChart';

const { Title } = Typography;

const DashboardPage: React.FC = () => {
  const currentUser = useAuthStore((state) => state.currentUser);

  const getStats = () => {
    if (currentUser?.role === 'admin') {
      return {
        clients: mockClients.length,
        documents: mockDocuments.length,
        audits: mockAudits.length,
        completedAudits: mockAudits.filter(a => a.status === 'completed').length,
      };
    } else {
      const clientDocs = mockDocuments.filter(d => d.clientId === currentUser?.clientId);
      const clientAudits = mockAudits.filter(a => a.clientId === currentUser?.clientId);
      return {
        documents: clientDocs.length,
        audits: clientAudits.length,
        completedAudits: clientAudits.filter(a => a.status === 'completed').length,
        pendingAudits: clientAudits.filter(a => a.status === 'pending').length,
      };
    }
  };

  const stats = getStats();

  return (
    <div>
      <Title level={2}>Dashboard</Title>
      <p style={{ marginBottom: 24 }}>
        Bienvenido, <strong>{currentUser?.name}</strong>
      </p>

      {currentUser?.role === 'admin' ? (
        <>
          <Row gutter={16}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Clientes"
                  value={stats.clients}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Documentos"
                  value={stats.documents}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Auditorías"
                  value={stats.audits}
                  prefix={<AuditOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Auditorías Completadas"
                  value={stats.completedAudits}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Gráficas para Admin */}
          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col span={12}>
              <Card>
                <AuditsPerMonthChart audits={mockAudits} />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <AuditsByClientChart audits={mockAudits} clients={mockClients} />
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Card>
                <AuditStatusChart audits={mockAudits} />
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Row gutter={16}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Mis Documentos"
                  value={stats.documents}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Mis Auditorías"
                  value={stats.audits}
                  prefix={<AuditOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Auditorías Pendientes"
                  value={stats.pendingAudits}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Gráficas para Cliente */}
          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card>
                <AuditStatusChart 
                  audits={mockAudits.filter(a => a.clientId === currentUser?.clientId)} 
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
